/** @format */

import { FC, useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  FileText,
  Loader2,
  Download,
  MessageCircle,
  Check,
  Trash2,
  Search,
} from "lucide-react";
import { useSopMedia } from "../../hooks/useSopMedia";
import { useMediaTranscription } from "../../hooks/useMediaTranscription";
import { useDocumentPreviewStore } from "../../store/useDocumentPreviewStore";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import {
  formatFeedbackCategoryLabel,
  type FeedbackItem,
} from "../../api/feedbackService";
import { PdfViewer } from "../PDF/PdfViewer";
import { MediaPlayer } from "../MediaPlayer";
import type { SourceSegment } from "../../types/sop";
import type { HighlightSegment, MediaPlayerHandle } from "../MediaPlayer";
import type { SOPMedia } from "../../api/sopMedia";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { cn } from "../../utils/cn";

export interface ReferencePanelStepSegment {
  videoId: string;
  segment: SourceSegment;
  title: string;
}

const DEFAULT_ASIDE_WIDTH = 288; // w-72
const MIN_ASIDE_WIDTH = 240;
const MAX_ASIDE_WIDTH = 850;

function labelForDocument(doc: {
  name?: string;
  s3_key: string;
  id: string;
}): string {
  if (doc.name?.trim()) return doc.name.trim();
  const parts = doc.s3_key.split("/");
  const raw = parts[parts.length - 1] || doc.id;
  // Strip UUID prefix added by the upload pipeline (e.g. "550e8400-...-446655440000_myfile.pdf")
  const cleaned = raw.replace(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
    "",
  );
  return decodeURIComponent(cleaned);
}

export interface ReferencePanelProps {
  sopId?: string | null;
  /** All steps that have sourceVideoId + sourceSegment; used to show every section on the video seek bar with title on hover */
  stepSegments?: ReferencePanelStepSegment[];
  /** Uploaded reference files (e.g. for regeneration); shown in a section so user can open and compare while reviewing candidates */
  referenceFiles?: File[];
  /** Version ID for the current SOP draft; used to fetch and show all suggestions for this version. */
  versionId?: string | null;
  /**
   * Default step key to show suggestions for, when no explicit step was chosen
   * via the toolbar (suggestionsStepKey). Typically the currently visible step.
   */
  defaultStepKey?: string | null;
  /** Called when user selects a suggestion; use to e.g. navigate to another step if feedback.stepIndex differs */
  onSelectFeedback?: (feedback: FeedbackItem) => void;
}

/** Suggestions tab: all feedback for the SOP version; one selectable (radio) per step. Selected item gets gradient and drives highlight. */
const ReferencePanelSuggestionsTab: FC<{
  sopId: string | null;
  versionId: string | null;
  onSelectFeedback?: (feedback: FeedbackItem) => void;
}> = ({ sopId, versionId, onSelectFeedback }) => {
  const feedbackByVersion = useFeedbackStore((s) => s.feedbackByVersion);
  const loading = useFeedbackStore((s) => s.loading);
  const selectedFeedbackIdByStep = useFeedbackStore(
    (s) => s.selectedFeedbackIdByStep,
  );
  const setSelectedFeedbackIdForStep = useFeedbackStore(
    (s) => s.setSelectedFeedbackIdForStep,
  );
  const updateFeedbackStatus = useFeedbackStore((s) => s.updateFeedbackStatus);
  const deleteFeedback = useFeedbackStore((s) => s.deleteFeedback);
  const { canManageFeedback } = useOrgRole();
  const { isSuperAdmin, currentUserId } = useSuperAdmin();
  const orphanedFeedbackIdsByStep = useFeedbackStore(
    (s) => s.orphanedFeedbackIdsByStep,
  );
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "resolved">("all");

  const versionKey = sopId && versionId ? `${sopId}:${versionId}` : null;
  const items = useMemo(
    () => (versionKey ? (feedbackByVersion[versionKey] ?? []) : []),
    [feedbackByVersion, versionKey],
  );

  const [confirmDelete, setConfirmDelete] = useState<{
    sopId: string;
    stepKey: string;
    feedbackId: string;
    isSelected: boolean;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredItems = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((fb) => fb.status === statusFilter);
  }, [items, statusFilter]);

  const openCount = useMemo(() => items.filter((f) => f.status === "open").length, [items]);
  const resolvedCount = useMemo(() => items.filter((f) => f.status === "resolved").length, [items]);

  const byStep = useMemo(() => {
    const map = new Map<number, FeedbackItem[]>();
    for (const fb of filteredItems) {
      const list = map.get(fb.stepIndex) ?? [];
      list.push(fb);
      map.set(fb.stepIndex, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [filteredItems]);

  useEffect(() => {
    if (!sopId || !versionId) return;
    const toClear: Array<{ stepKey: string; feedbackId: string }> = [];
    for (const fb of items) {
      const stepKey = `${sopId}:${versionId}:${fb.stepIndex}`;
      // Only auto-clear selections that are stale per persisted DB flag.
      // Derived orphaned IDs can be transient (and previously caused double-click selection).
      if (fb.selectionStale !== true) continue;
      if ((selectedFeedbackIdByStep[stepKey] ?? null) === fb.id) {
        toClear.push({ stepKey, feedbackId: fb.id });
      }
    }
    if (toClear.length === 0) return;
    for (const x of toClear) {
      setSelectedFeedbackIdForStep(x.stepKey, null);
    }
  }, [
    items,
    orphanedFeedbackIdsByStep,
    selectedFeedbackIdByStep,
    sopId,
    versionId,
    setSelectedFeedbackIdForStep,
  ]);

  const handleSelect = useCallback(
    (fb: FeedbackItem) => {
      if (!sopId || !versionId) return;
      const stepKey = `${sopId}:${versionId}:${fb.stepIndex}`;
      const isStale = fb.selectionStale === true;
      if (isStale) return;
      const selectedId = selectedFeedbackIdByStep[stepKey] ?? null;
      const nextId = selectedId === fb.id ? null : fb.id;
      setSelectedFeedbackIdForStep(stepKey, nextId);
      if (nextId != null) onSelectFeedback?.(fb);
    },
    [
      sopId,
      versionId,
      selectedFeedbackIdByStep,
      setSelectedFeedbackIdForStep,
      onSelectFeedback,
    ],
  );

  if (!sopId || !versionId) {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-auto px-3 py-3">
        <div className="rounded-lg border border-dashed border-slate-500/50 bg-slate-900/40 min-h-[120px] flex flex-col items-center justify-center gap-2 p-4 text-center">
          <MessageCircle className="w-10 h-10 text-slate-500" />
          <p className="text-slate-500 text-sm">
            Open an SOP version to see feedback.
          </p>
        </div>
      </div>
    );
  }

  if (loading && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-auto px-3 py-3">
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin" aria-hidden />
          <p className="text-sm">Loading feedback...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-auto px-3 py-3">
        <div className="rounded-lg border border-dashed border-slate-500/50 bg-slate-900/40 min-h-[120px] flex flex-col items-center justify-center gap-2 p-4 text-center">
          <MessageCircle className="w-10 h-10 text-slate-500" />
          <p className="text-slate-500 text-sm">No feedback for this SOP.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-3 py-3 gap-3">
      {/* Status filter */}
      <div className="flex w-full rounded-lg bg-slate-900/60 p-0.5 shrink-0">
        {(["all", "open", "resolved"] as const).map((f) => {
          const count = f === "all" ? items.length : f === "open" ? openCount : resolvedCount;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={`flex flex-1 items-center justify-center gap-1 rounded-md px-1 py-1 text-sm font-medium transition-colors capitalize ${
                statusFilter === f
                  ? "bg-slate-700 text-slate-100 shadow"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {f}
              <span className={`text-xs ${statusFilter === f ? "text-slate-400" : "text-slate-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>
      <div className="flex-1 overflow-auto">
      <div className="space-y-4">
        {byStep.map(([stepIndex, stepItems]) => {
          const stepKey = `${sopId}:${versionId}:${stepIndex}`;
          const selectedId = selectedFeedbackIdByStep[stepKey] ?? null;
          const stepSectionLabel =
            stepIndex === -1 ? "Title page" : `Step ${stepIndex + 1}`;
          return (
            <div key={stepIndex}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                {stepSectionLabel}
              </p>
              <ul
                className="space-y-2"
                role="radiogroup"
                aria-label={`Suggestions for ${stepSectionLabel}`}
              >
                {stepItems.map((fb) => {
                  const isSelected = selectedId === fb.id;
                  const isResolved = fb.status === "resolved";
                  // Stale only matters for open feedback — resolved takes priority.
                  const isStale = fb.selectionStale === true && !isResolved;
                  const isOrphaned =
                    !isResolved &&
                    (orphanedFeedbackIdsByStep[stepKey] ?? []).includes(fb.id);
                  // Resolved items remain clickable so the user can select/deselect
                  // the highlight in the editor (same toggle behaviour as open items).
                  const isClickable = !isStale && !isOrphaned;
                  return (
                    <li key={fb.id}>
                      <div
                        role="group"
                        aria-label="Suggestion item"
                        className={`group w-full text-left rounded-xl border px-3 py-2.5 transition-all ${
                          isSelected
                            ? "border-purple-500/50 bg-gradient-to-r from-purple-500/22 via-purple-500/12 to-pink-500/18 shadow-sm shadow-purple-500/10"
                            : isResolved
                              ? "border-emerald-700/40 bg-emerald-900/10 opacity-80 hover:bg-emerald-900/20 hover:border-emerald-600/50"
                              : isStale || isOrphaned
                                ? "border-slate-700/60 bg-slate-900/30 opacity-80"
                                : "border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/40 hover:border-slate-500/60"
                        } ${isClickable ? "cursor-pointer" : "cursor-default"}`}
                        {...(isClickable
                          ? {
                              tabIndex: 0,
                              onClick: () => {
                                handleSelect(fb);
                              },
                              onKeyDown: (e: React.KeyboardEvent) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleSelect(fb);
                                }
                              },
                            }
                          : {
                              tabIndex: -1,
                              "aria-disabled": true,
                            })}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex flex-col min-w-0 flex-1 w-full">
                            <div className="flex items-center gap-2 flex-wrap">
                              {isSuperAdmin && (
                                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                  {formatFeedbackCategoryLabel(fb)}
                                </span>
                              )}
                              <span
                                className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ml-auto ${
                                  isResolved
                                    ? "bg-emerald-500/20 text-emerald-400"
                                    : isStale
                                      ? "bg-violet-500/20 text-violet-400"
                                      : "bg-amber-500/20 text-amber-400"
                                }`}
                              >
                                {isResolved
                                  ? "Resolved"
                                  : isStale
                                    ? "Stale"
                                    : "Open"}
                              </span>
                              {isSuperAdmin && fb.selectedForRegenerationAt && (
                                <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">
                                  Included In Update
                                </span>
                              )}
                            </div>

                            <p className="text-sm leading-snug text-slate-100 mt-1 line-clamp-2">
                              {fb.comment}
                            </p>
                            {fb.selectedText && (
                              <p className="text-xs italic text-slate-400 mt-1 truncate">
                                &ldquo;{fb.selectedText}&rdquo;
                              </p>
                            )}
                          </div>
                        </div>
                        {(canManageFeedback || fb.createdBy === currentUserId) && (
                          <div className="shrink-0">
                            <div className="flex items-center justify-end gap-1.5">
                              {canManageFeedback && (
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!sopId) return;
                                    if (fb.status === "resolved") return;
                                    await updateFeedbackStatus(
                                      sopId,
                                      fb.id,
                                      "resolved",
                                    );
                                  }}
                                  disabled={fb.status === "resolved"}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-900/30 text-slate-300 hover:bg-slate-800/60 hover:text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                  title={
                                    fb.status === "resolved"
                                      ? "Already resolved"
                                      : "Resolve feedback"
                                  }
                                >
                                  <Check className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!sopId) return;
                                  setConfirmDelete({
                                    sopId,
                                    stepKey,
                                    feedbackId: fb.id,
                                    isSelected,
                                  });
                                }}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-900/30 text-slate-300 hover:bg-slate-800/60 hover:text-red-300"
                                title="Delete feedback"
                              >
                                <Trash2 className="h-4 w-4" aria-hidden />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      </div>

      <ConfirmDialog
        open={confirmDelete != null}
        onClose={() => {
          if (isDeleting) return;
          setConfirmDelete(null);
        }}
        onConfirm={async () => {
          if (!confirmDelete) return;
          setIsDeleting(true);
          try {
            await deleteFeedback(confirmDelete.sopId, confirmDelete.feedbackId);
            if (confirmDelete.isSelected) {
              setSelectedFeedbackIdForStep(confirmDelete.stepKey, null);
            }
          } finally {
            setIsDeleting(false);
            setConfirmDelete(null);
          }
        }}
        title="Delete feedback?"
        description="This will permanently delete this feedback."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export const ReferencePanel: FC<ReferencePanelProps> = ({
  sopId,
  stepSegments = [],
  referenceFiles = [],
  versionId = null,
  onSelectFeedback,
}) => {
  const { isSuperAdmin } = useSuperAdmin();
  const asideCollapsed = useDocumentPreviewStore((s) => s.asideCollapsed);
  const setAsideCollapsed = useDocumentPreviewStore((s) => s.setAsideCollapsed);
  const asideActiveTab = useDocumentPreviewStore((s) => s.asideActiveTab);
  const setAsideActiveTab = useDocumentPreviewStore((s) => s.setAsideActiveTab);
  const [asideWidth, setAsideWidth] = useState(DEFAULT_ASIDE_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [fileQuery, setFileQuery] = useState("");
  const resizeStartRef = useRef({ x: 0, w: 0 });
  const mediaPlayerRef = useRef<MediaPlayerHandle>(null);
  const didMountRef = useRef(false);

  const referenceFileKey = useMemo(
    () => referenceFiles.map((f) => `${f.name}:${f.size}`).join(";"),
    [referenceFiles],
  );
  useEffect(() => {
    const urls = referenceFiles.map((f) => URL.createObjectURL(f));
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [referenceFileKey, referenceFiles]);

  const selectedFileId = useDocumentPreviewStore((s) =>
    sopId ? (s.bySopId[sopId]?.selectedFileId ?? "") : "",
  );
  const setSelectedFileId = useDocumentPreviewStore((s) => s.setSelectedFileId);
  const getVideoTimestamp = useDocumentPreviewStore((s) => s.getVideoTimestamp);
  const setVideoTimestamp = useDocumentPreviewStore((s) => s.setVideoTimestamp);
  const focusedSegment = useDocumentPreviewStore((s) =>
    sopId ? (s.bySopId[sopId]?.focusedSegment ?? null) : null,
  );
  const shouldFetchPreviewMedia =
    asideActiveTab === "preview" && (filePickerOpen || Boolean(selectedFileId));
  const { media, loading, error } = useSopMedia(sopId ?? null, {
    enabled: shouldFetchPreviewMedia,
    includeExtracted: false,
  });

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      resizeStartRef.current = { x: e.clientX, w: asideWidth };
      setIsResizing(true);
    },
    [asideWidth],
  );

  const handleResizeMove = useCallback((e: PointerEvent) => {
    const { x: startX, w: startW } = resizeStartRef.current;
    const deltaX = startX - e.clientX; // drag left = positive delta = wider
    const next = Math.min(
      MAX_ASIDE_WIDTH,
      Math.max(MIN_ASIDE_WIDTH, startW + deltaX),
    );
    setAsideWidth(next);
  }, []);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    const style = document.body.style;
    const prevCursor = style.cursor;
    const prevSelect = style.userSelect;
    style.cursor = "col-resize";
    style.userSelect = "none";
    window.addEventListener("pointermove", handleResizeMove);
    window.addEventListener("pointerup", handleResizeEnd);
    window.addEventListener("pointercancel", handleResizeEnd);
    return () => {
      style.cursor = prevCursor;
      style.userSelect = prevSelect;
      window.removeEventListener("pointermove", handleResizeMove);
      window.removeEventListener("pointerup", handleResizeEnd);
      window.removeEventListener("pointercancel", handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Wire focused segment changes to video seek + play
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!focusedSegment || focusedSegment.videoId !== selectedFileId) return;
    mediaPlayerRef.current?.seekTo(focusedSegment.segment.startTimeSeconds);
    mediaPlayerRef.current?.play().catch(() => {});
  }, [focusedSegment, selectedFileId]);

  const documents = useMemo(() => media?.documents ?? [], [media?.documents]);
  const images = useMemo(() => media?.images ?? [], [media?.images]);
  const videos = useMemo(() => media?.videos ?? [], [media?.videos]);
  const previewItems = useMemo(
    (): SOPMedia[] => [...documents, ...images, ...videos],
    [documents, images, videos],
  );

  useEffect(() => {
    if (!sopId || selectedFileId === "") return;
    const isValid = previewItems.some((p) => p.id === selectedFileId);
    if (!isValid) {
      setSelectedFileId(sopId, "");
    }
  }, [sopId, previewItems, selectedFileId, setSelectedFileId]);

  const selectedMediaItem = useMemo(
    () => previewItems.find((m) => m.id === selectedFileId),
    [previewItems, selectedFileId],
  );
  const { transcription: fetchedTranscription } = useMediaTranscription(
    sopId ?? null,
    selectedMediaItem?.type === "video" ? selectedFileId : null,
    {
      skip:
        !sopId ||
        !selectedFileId ||
        selectedMediaItem?.type !== "video" ||
        !!selectedMediaItem?.transcription,
    },
  );
  const fileOptions = useMemo(() => {
    return previewItems.map((doc) => ({
      id: doc.id,
      label: labelForDocument(doc),
      fileType: doc.type,
      includedInUpdate: Boolean(doc.selected_for_regeneration_at),
    }));
  }, [previewItems]);

  const selected = useMemo(
    () => fileOptions.find((f) => f.id === selectedFileId) ?? null,
    [fileOptions, selectedFileId],
  );
  const filteredFileOptions = useMemo(() => {
    const query = fileQuery.trim().toLowerCase();
    if (!query) return fileOptions;
    return fileOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [fileOptions, fileQuery]);
  const selectedDoc = previewItems.find((d) => d.id === selectedFileId);
  const badgeLabelForType = useCallback(
    (type: string) => {
      if (type === "step_file") return "STEP";
      return type.replace("_", " ").toUpperCase();
    },
    [],
  );

  const handleDownload = useCallback(async () => {
    if (!selectedDoc?.url) return;
    const filename = labelForDocument(selectedDoc);
    try {
      const res = await fetch(selectedDoc.url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(selectedDoc.url, "_blank", "noopener,noreferrer");
    }
  }, [selectedDoc]);
  const isPdf =
    selectedDoc?.file_type === "application/pdf" ||
    selectedDoc?.url?.toLowerCase().endsWith(".pdf");
  const isImage =
    selectedDoc?.type === "image" ||
    selectedDoc?.type === "render" ||
    (selectedDoc?.file_type?.startsWith("image/") ?? false);
  const isVideo =
    selectedDoc?.type === "video" ||
    (selectedDoc?.file_type?.startsWith("video/") ?? false);
  const isAudio = selectedDoc?.file_type?.startsWith("audio/") ?? false;
  const videoTranscription =
    selectedDoc?.transcription ?? fetchedTranscription ?? null;

  const videoHighlights = useMemo((): HighlightSegment[] => {
    if (!isVideo) return [];
    const forThisVideo = stepSegments.filter(
      (s) => s.videoId === selectedFileId,
    );
    return forThisVideo.map((s) => ({
      startTime: s.segment.startTimeSeconds,
      endTime: s.segment.endTimeSeconds,
      title: s.title,
      active:
        !!focusedSegment &&
        focusedSegment.videoId === selectedFileId &&
        focusedSegment.segment.startTimeSeconds === s.segment.startTimeSeconds,
    }));
  }, [isVideo, selectedFileId, stepSegments, focusedSegment]);

  return (
    <aside
      className="shrink-0 border-l border-slate-600/50 bg-slate-800/40 flex flex-col relative overflow-hidden transition-[width] duration-200 ease-in-out"
      style={{ width: asideCollapsed ? 40 : asideWidth }}
      title="Reference panel"
    >
      <div
        className={`absolute inset-0 flex h-full flex-col items-center py-4 transition-opacity duration-150 ease-out ${
          asideCollapsed
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
          <button
            type="button"
            onClick={() => setAsideCollapsed(false)}
            className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
            aria-label="Expand Reference panel"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-[10px] text-slate-500 mt-2 [writing-mode:vertical-rl] rotate-180 select-none">
            Reference panel
          </span>
      </div>
      <div
        className={`h-full flex flex-col transition-opacity duration-150 ease-out ${
          asideCollapsed
            ? "opacity-0 pointer-events-none"
            : "opacity-100 pointer-events-auto"
        }`}
      >
          {/* Resize handle on the left edge */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            onPointerDown={handleResizeStart}
            className={`absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize touch-none z-10 flex-shrink-0 ${isResizing ? "bg-slate-500/80" : "hover:bg-slate-500/50"} transition-colors`}
            title="Drag to resize"
          />
          <div className="px-3 py-4 border-b border-slate-600/50 flex items-center justify-between gap-2 pt-4">
            <h3 className="text-sm font-semibold text-slate-400 shrink-0">
              Reference panel
            </h3>
            <button
              type="button"
              onClick={() => setAsideCollapsed(true)}
              className="p-1.5 rounded-md text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 transition-colors shrink-0"
              aria-label="Collapse Reference panel"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Tabs: same segmented style as Title page aside (Versions / Revisions / Materials) */}
          <div className="px-3 py-2 border-b border-slate-600/50">
            <div className="flex w-full rounded-lg bg-slate-900/60 p-0.5">
              <button
                type="button"
                onClick={() => setAsideActiveTab("preview")}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-1 py-1 text-sm font-medium transition-colors min-w-0 ${
                  asideActiveTab === "preview"
                    ? "bg-slate-700 text-slate-100 shadow"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <FileText className="h-4 w-4 shrink-0" aria-hidden />
                Files
              </button>
              <button
                type="button"
                onClick={() => setAsideActiveTab("suggestions")}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-1 py-1 text-sm font-medium transition-colors min-w-0 ${
                  asideActiveTab === "suggestions"
                    ? "bg-slate-700 text-slate-100 shadow"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
                Feedback
              </button>
            </div>
          </div>
          {asideActiveTab === "preview" && (
            <>
          <div className="relative z-40 px-3 py-2 border-b border-slate-600/50 flex items-center gap-2">
            <div className="relative flex-1 min-w-0 z-40">
              <button
                type="button"
                disabled={loading || !sopId}
                onClick={() => setFilePickerOpen((v) => !v)}
                className="w-full bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-left">
                    {selected?.label ?? "Select a file..."}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </span>
              </button>
              {filePickerOpen && (
                <div className="absolute z-[120] mt-1 w-full rounded-md border border-slate-600/70 bg-slate-900/95 shadow-xl">
                  <div className="p-2 border-b border-slate-700/60">
                    <div className="flex items-center gap-2 rounded-md border border-slate-700/60 bg-slate-950/70 px-2 py-1.5">
                      <Search className="w-4 h-4 text-slate-500" />
                      <input
                        value={fileQuery}
                        onChange={(e) => setFileQuery(e.target.value)}
                        placeholder="Search files..."
                        className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="modal-body-scroll max-h-64 overflow-y-auto">
                    {filteredFileOptions.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">
                        {loading ? "Loading files..." : "No files found."}
                      </div>
                    ) : (
                      filteredFileOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            if (!sopId) return;
                            setSelectedFileId(sopId, option.id);
                            setFilePickerOpen(false);
                          }}
                          className={cn("w-full text-left px-3 py-2 hover:bg-slate-800/70 transition-colors", {
                            "bg-slate-600/70": selectedFileId === option.id,
                          })}
                        >
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm text-slate-100 flex-1">
                              {option.label}
                            </span>
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0 bg-slate-600/30 text-slate-200 border border-slate-500/40">
                              {badgeLabelForType(option.fileType)}
                            </span>
                            {isSuperAdmin && option.includedInUpdate && (
                              <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40">
                                Included in update
                              </span>
                            )}
                            {/* {selectedFileId === option.id && (
                              <Check className="w-4 h-4 text-emerald-300 shrink-0" />
                            )} */}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {selectedDoc && (
              <button
                type="button"
                onClick={handleDownload}
                className="p-2 rounded-md text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 transition-colors shrink-0"
                aria-label="Download file"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="relative z-0 flex-1 flex flex-col min-h-0 overflow-auto px-3 py-3">
            {loading && (
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin" aria-hidden />
                <p className="text-sm">Loading documents...</p>
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
                {error}
              </div>
            )}
            {!loading && !error && !sopId && (
              <div className="rounded-lg border border-dashed border-slate-500/50 bg-slate-900/40 w-full min-h-[180px] flex flex-col items-center justify-center gap-2 p-4 text-center">
                <FileText className="w-12 h-12 text-slate-500" aria-hidden />
                <p className="text-slate-500 text-sm">
                  Open an SOP to see reference documents.
                </p>
              </div>
            )}
            {!loading && !error && sopId && previewItems.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-500/50 bg-slate-900/40 w-full min-h-[180px] flex flex-col items-center justify-center gap-2 p-4 text-center">
                <FileText className="w-12 h-12 text-slate-500" aria-hidden />
                <p className="text-slate-500 text-sm">
                  No reference documents for this SOP.
                </p>
                <p className="text-slate-600 text-xs">
                  Add documents via sop_media_references to show them here.
                </p>
              </div>
            )}
            {!loading &&
              !error &&
              sopId &&
              previewItems.length > 0 &&
              !selectedDoc && (
                <div className="rounded-lg border border-dashed border-slate-500/50 bg-slate-900/40 w-full min-h-[180px] flex flex-col items-center justify-center gap-2 p-4 text-center">
                  <FileText className="w-12 h-12 text-slate-500" aria-hidden />
                  <p className="text-slate-500 text-sm">
                    Select a file to preview.
                  </p>
                </div>
              )}
            {!loading && !error && selectedDoc && (
              <div className="rounded-lg border border-slate-600 bg-slate-900/40 w-full min-h-[200px] overflow-hidden flex flex-col">
                {isPdf && (
                  <div className="flex flex-col flex-1 min-h-0 min-w-0">
                    <PdfViewer
                      files={[
                        {
                          id: selectedDoc.id,
                          fileName: labelForDocument(selectedDoc).endsWith(
                            ".pdf",
                          )
                            ? labelForDocument(selectedDoc)
                            : `${labelForDocument(selectedDoc)}.pdf`,
                          signedUrl: selectedDoc.url,
                        },
                      ]}
                      showControls={true}
                      className="rounded-b-lg overflow-hidden"
                    />
                  </div>
                )}
                {isImage && (
                  <img
                    src={selectedDoc.url}
                    alt={selected?.label ?? "Preview"}
                    className="w-full max-h-[400px] object-contain bg-slate-900"
                  />
                )}
                {isVideo && (
                  <MediaPlayer
                    ref={mediaPlayerRef}
                    src={selectedDoc.url}
                    variant="video"
                    title={selected?.label}
                    transcription={videoTranscription}
                    initialTime={
                      sopId
                        ? (getVideoTimestamp(sopId, selectedDoc.id) ?? 0)
                        : 0
                    }
                    onTimeUpdate={
                      sopId
                        ? (t) => setVideoTimestamp(sopId, selectedDoc.id, t)
                        : undefined
                    }
                    highlightSegments={videoHighlights}
                  />
                )}
                {isAudio && (
                  <MediaPlayer
                    src={selectedDoc.url}
                    variant="audio"
                    title={selected?.label}
                  />
                )}
                {!isPdf && !isImage && !isVideo && !isAudio && (
                  <div className="flex flex-col items-center justify-center gap-2 p-6 text-center min-h-[180px]">
                    <FileText
                      className="w-12 h-12 text-slate-500"
                      aria-hidden
                    />
                    <p className="text-slate-500 text-sm">
                      Preview not available for this file type.
                    </p>
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="text-sm text-slate-400 hover:text-slate-200 underline inline-flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      Download file
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* {referenceFiles.length > 0 &&
              referenceFileUrls.length === referenceFiles.length && (
                <div className="mt-3 pt-3 border-t border-slate-600/50 space-y-2">
                  <p className="text-xs font-medium text-slate-400">
                    Reference uploads
                  </p>
                  <ul className="space-y-1">
                    {referenceFiles.map((file, i) => (
                      <li key={`${file.name}-${file.size}`}>
                        <a
                          href={referenceFileUrls[i]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-slate-300 hover:text-slate-100 underline truncate block"
                          title={file.name}
                        >
                          {file.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )} */}
          </div>
            </>
          )}
          {asideActiveTab === "suggestions" && (
            <ReferencePanelSuggestionsTab
              sopId={sopId ?? null}
              versionId={versionId ?? null}
              onSelectFeedback={onSelectFeedback}
            />
          )}
      </div>
    </aside>
  );
};
